import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "size"];
import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { withCrossAppID } from '../crossAppID';
import Brand from '../Brand';
import Boundary from '../Boundary';
import Dropdown from '../Dropdown';
import { sizes } from './constants';
import NavbarSection from './NavbarSection';
import NavbarItem from './NavbarItem';
import NavbarAccountProvider from './NavbarAccountProvider';
import NavbarAccountItem from './NavbarAccountItem';
import NavbarAccountInfo from './NavbarAccountInfo';
const DISPLAY_NAME = 'Navbar';
const DEFAULT_HEIGHT = '64px';
const SMALL_HEIGHT = '44px';
const DROPDOWN_MAX_HEIGHT = 430;
const Root = styled.header.withConfig({
  displayName: "Navbar__Root",
  componentId: "sc-gmzmi2-0"
})(["position:fixed;height:", ";width:100vw;top:0;left:0;align-items:stretch;background-color:var(--neutral-surface);box-shadow:var(--shadow-sm);z-index:var(--zindex-2);", " &,> *{display:flex;flex-wrap:nowrap;}", "{order:0;padding-right:var(--size-spacing-04);padding-left:var(--size-spacing-04);height:auto;> *{height:32px;}}"], DEFAULT_HEIGHT, _ref => {
  let {
    $size
  } = _ref;
  return $size === sizes.sm && css(["height:", ";"], SMALL_HEIGHT);
}, Brand.Style);
const BaseNavbar = withCrossAppID(DISPLAY_NAME, /*#__PURE__*/forwardRef((_ref2, ref) => {
  let {
      children,
      size
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded);
  return /*#__PURE__*/React.createElement(Boundary, {
    name: DISPLAY_NAME
  }, /*#__PURE__*/React.createElement(Dropdown.Provider, {
    maxHeight: DROPDOWN_MAX_HEIGHT
  }, /*#__PURE__*/React.createElement(Root, _extends({}, rest, {
    $size: size,
    ref: ref
  }), children)));
}));

/*
 * We need to make this because the `react-docgen` lib doesn't get the component data that uses HOC.
 * It's an old issue from the library:
 * - https://github.com/reactjs/react-docgen/issues/177
 * - https://github.com/reactjs/react-docgen/issues/205
 * - https://github.com/reactjs/react-docgen/issues/288
 *
 * The workaround is to define a base component with HOC (that will not be documented) and define a second component that uses the base component.
 * This way the `react-docgen` will get the data from the second component and will document correctly.
 */
const Navbar = props => /*#__PURE__*/React.createElement(BaseNavbar, props);
Navbar.displayName = DISPLAY_NAME;
Navbar.Style = Root;
Navbar.Section = NavbarSection;
Navbar.Item = NavbarItem;
Navbar.AccountProvider = NavbarAccountProvider;
Navbar.AccountItem = NavbarAccountItem;
Navbar.AccountInfo = NavbarAccountInfo;
Navbar.sizes = sizes;
Navbar.defaultProps = {
  size: Navbar.sizes.md
};
process.env.NODE_ENV !== "production" ? Navbar.propTypes = {
  /**
   * Sets children.
   */
  children: PropTypes.node,
  /**
   * Sets the Navbar size.
   */
  size: PropTypes.oneOf([Navbar.sizes.md, Navbar.sizes.sm])
} : void 0;
export default Navbar;